import { useEffect, useState } from 'react';
import type {
  ActionId,
  HabitCategory,
  IsoDate,
  ObjectiveCategory,
  ObjectiveId,
  ObjectiveLevelOfEffort,
  ObjectivePriority,
  ObjectiveReminder,
  ObjectiveView,
} from '@contracts';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { ChevronLeft, Plus } from '@ui/shared/icons';
import { DatePickerField } from '@ui/shared/primitives/DatePickerField';
import { SkeletonBlock } from '@ui/shared/primitives/Skeleton';
import { localIsoDate } from '@ui/shared/format/date';
import {
  ActionRow,
  Field,
  PrimaryButton,
  SecondaryButton,
  inputStyle,
  validationHelperStyle,
} from '@ui/shared/primitives/FormControls';
import {
  CadenceStepper,
  CategoryPillSelect,
  EffortBars,
  EffortTileSelector,
  PrioritySegmentedControl,
  ReminderSchedulePicker,
  StatusSegmentedControl,
} from '@ui/desktop/objective-form/FormPrimitives';
import { ActionStatusPill, type ActionDisplayStatus } from '@ui/desktop/objective-form/ActionStatusPill';
import { LinkedHabitsSection } from '@ui/phone/objective-detail/LinkedHabitsSection';
import { PinnedToggleSection } from '@ui/desktop/objective-form/PinnedToggleSection';
import { VisibilityTogglesSection } from '@ui/desktop/objective-form/VisibilityTogglesSection';
import { ObjectiveLifecycleFooter } from './ObjectiveLifecycleFooter';

/**
 * "Edit objective" — the other half of closing the "can't do anything after
 * first-run" gap (owner direction). Pre-filled version of the same
 * title/why/date/actions field set as NewObjectiveScreen/FirstRunSetup (same
 * shared FormControls), wired to the edit commands that already exist on
 * DomainApi (editObjective for title/why, adjustObjectiveTargetDateManually
 * for the date, addAction/editAction/removeAction for the action list) —
 * this screen is UI-only; no new domain logic. Minimal and functional per
 * explicit direction, not a polished management dashboard: title/why/date
 * save together on "Save changes"; each action row saves/removes
 * immediately on blur/click (same one-tap-per-change feel as the rest of
 * the app — PRD's "state persists instantly, no save buttons" rule applies
 * to the actions list even though the top fields use one explicit save,
 * since actions are already a list of independent items elsewhere too).
 *
 * Category/level of effort/cadence goal/priority/reminder/status/pinned/
 * visibility-toggle/three-state-action-status/per-action-effort fields
 * (owner-confirmed phone parity subset — see NewObjectiveScreen's own header
 * comment) reuse `ObjectiveFormScreenDesktop`'s Phase 5 field primitives as
 * pure imports and follow this screen's OWN existing convention (unlike the
 * header's explicit "Save changes" button): every change persists
 * immediately via the matching DomainApi command, with no local echo state
 * beyond what's needed for text-input responsiveness — each control simply
 * reads off the freshly-fetched `objective` and the `version`-driven refetch
 * effect above keeps it in sync after every command resolves.
 */
export function EditObjectiveScreen({
  objectiveId,
  onDone,
  onCancel,
  onArchived,
}: {
  readonly objectiveId: string;
  readonly onDone: () => void;
  readonly onCancel: () => void;
  /**
   * Archiving (objectives.md v2.0 §7.1/MC-1) navigates differently than a
   * normal save: an archived objective is no longer "the" objective being
   * edited, so there's nothing on the detail screen to return to (§7.3) —
   * this fires instead of onDone specifically for that transition. Optional
   * so existing/future callers that never wire the archive footer aren't
   * forced to supply a second destination.
   */
  readonly onArchived?: () => void;
}) {
  const api = useDomainApi();
  const version = useDomainVersion();
  const [objective, setObjective] = useState<ObjectiveView | null>(null);
  const [title, setTitle] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [why, setWhy] = useState('');
  const [targetDate, setTargetDate] = useState<IsoDate | null>(null);
  const [savingHeader, setSavingHeader] = useState(false);
  const [newActionTitle, setNewActionTitle] = useState('');
  const [addingAction, setAddingAction] = useState(false);
  const [habitsById, setHabitsById] = useState<
    ReadonlyMap<string, { readonly category: HabitCategory; readonly habitKind: 'build' | 'avoid' }>
  >(new Map());

  useEffect(() => {
    let cancelled = false;
    void api.getObjective(objectiveId as ObjectiveId).then((obj) => {
      if (cancelled) return;
      setObjective(obj);
      setTitle(obj.title);
      setWhy(obj.why);
      setTargetDate(obj.targetDate);
    });
    return () => {
      cancelled = true;
    };
    // Only re-fetch when the objectiveId changes or the log advances
    // (version) — NOT when local field state changes, so typing doesn't
    // fight a refetch.
  }, [api, objectiveId, version]);

  // Linked-habits display join (same pattern as ObjectiveDetailScreen.tsx
  // and ObjectiveFormScreenDesktop.tsx's own habitsById build): category and
  // habitKind aren't carried on ObjectiveView.linkedHabits itself, so they're
  // joined in from a separate listHabits() read.
  useEffect(() => {
    void api.listHabits().then((habits) => {
      setHabitsById(new Map(habits.map((h) => [h.habitId, { category: h.category, habitKind: h.plan.habitKind }])));
    });
  }, [api, version]);

  if (!objective || targetDate === null) {
    return (
      <div style={{ padding: 'var(--space-6) var(--layout-page-margin-phone) 0' }}>
        <SkeletonBlock height={20} width={80} />
        <div style={{ height: 'var(--space-4)' }} />
        <SkeletonBlock height={32} />
        <div style={{ height: 'var(--space-6)' }} />
        <SkeletonBlock height={60} />
      </div>
    );
  }

  // Bug fix (timezone): toISOString() reads UTC, not device-local time
  // (ADR-0006) — in the evening, in any timezone behind UTC, that silently
  // reported TOMORROW as "today" to this date picker's "Today" quick-select
  // and its isToday/isPast cell styling (same bug class fixed in
  // TodayView.tsx). localIsoDate() mirrors the domain layer's own
  // ClockPort.
  const today = localIsoDate();
  const headerDirty = title.trim() !== objective.title || why.trim() !== objective.why || targetDate !== objective.targetDate;
  const canSaveHeader = title.trim().length > 0 && headerDirty && !savingHeader;

  async function saveHeader() {
    setTitleTouched(true);
    if (!canSaveHeader || !objective) return;
    setSavingHeader(true);
    try {
      const changes: { title?: string; why?: string } = {};
      if (title.trim() !== objective.title) changes.title = title.trim();
      if (why.trim() !== objective.why) changes.why = why.trim();
      if (Object.keys(changes).length > 0) {
        await api.editObjective(objective.objectiveId, changes);
      }
      if (targetDate !== null && targetDate !== objective.targetDate) {
        await api.adjustObjectiveTargetDateManually(objective.objectiveId, targetDate);
      }
      onDone();
    } finally {
      setSavingHeader(false);
    }
  }

  // -- Phase-6-parity field handlers (owner-confirmed subset) -----------------
  // Every handler persists immediately via updateObjectiveDetails/
  // setObjectiveStatus/setObjectivePinned (this screen's existing
  // per-action-row instant-persist convention, not the header's explicit
  // "Save changes" button) — no local echo state; the version-driven refetch
  // effect above keeps `objective` current after each command resolves.

  function handleCategoryChange(next: ObjectiveCategory) {
    if (!objective) return;
    void api.updateObjectiveDetails(objective.objectiveId, { category: next });
  }

  function handleEffortChange(next: ObjectiveLevelOfEffort) {
    if (!objective) return;
    void api.updateObjectiveDetails(objective.objectiveId, { levelOfEffort: next });
  }

  function handleCadenceChange(next: number | null) {
    if (!objective) return;
    void api.updateObjectiveDetails(objective.objectiveId, { cadenceGoalPerWeek: next });
  }

  function handlePriorityChange(next: ObjectivePriority) {
    if (!objective) return;
    void api.updateObjectiveDetails(objective.objectiveId, { priority: next });
  }

  function handleReminderChange(next: ObjectiveReminder | null) {
    if (!objective) return;
    void api.updateObjectiveDetails(objective.objectiveId, { reminder: next });
  }

  function handleStatusChange(next: 'active' | 'on_hold') {
    if (!objective) return;
    void api.setObjectiveStatus(objective.objectiveId, next);
  }

  function handlePinnedChange(next: boolean) {
    if (!objective) return;
    void api.setObjectivePinned(objective.objectiveId, next, null);
  }

  function handleVisibilityChange(changes: {
    readonly includeInWeeklyReview?: boolean;
    readonly surfaceOnToday?: boolean;
    readonly ambientRewardOnComplete?: boolean;
  }) {
    if (!objective) return;
    void api.updateObjectiveDetails(objective.objectiveId, changes);
  }

  // Replicates ObjectiveFormScreenDesktop's handleSetActionStatus exactly:
  // 'done' goes through completeAction (celebration-eligible); reverting a
  // done action back to to_do/doing goes through revertLogEntry (only when
  // entryEventId exists); everything else goes through the plain
  // to_do/doing setActionStatus command.
  async function handleSetActionStatus(actionId: ActionId, status: ActionDisplayStatus) {
    if (!objective) return;
    const current = objective.actions.find((a) => a.actionId === actionId);
    if (!current) return;

    if (status === 'done') {
      await api.completeAction(objective.objectiveId, actionId, today as IsoDate);
      return;
    }

    if (current.status === 'done') {
      if (current.entryEventId) {
        await api.revertLogEntry(current.entryEventId);
      }
      return;
    }

    await api.setActionStatus(objective.objectiveId, actionId, status);
  }

  async function handleSetActionEffort(actionId: ActionId, effortMinutes: number | null) {
    if (!objective) return;
    await api.editAction(objective.objectiveId, actionId, { effortMinutes });
  }

  async function addAction() {
    const value = newActionTitle.trim();
    if (value.length === 0 || !objective) return;
    setAddingAction(true);
    try {
      await api.addAction(objective.objectiveId, {
        title: value,
        dueDate: null,
        milestoneTag: null,
        position: null,
      });
      setNewActionTitle('');
    } finally {
      setAddingAction(false);
    }
  }

  return (
    <main
      aria-label={`Edit objective: ${objective.title}`}
      style={{ padding: 'var(--space-6) var(--layout-page-margin-phone) var(--space-8)', maxWidth: '480px', margin: '0 auto' }}
    >
      <button
        type="button"
        onClick={onCancel}
        aria-label="Back to objective"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-1)',
          color: 'var(--text-muted)',
          fontSize: 'var(--type-caption-size)',
          fontWeight: 600,
          minHeight: 'var(--touch-target-min)',
          marginLeft: '-8px',
        }}
      >
        <ChevronLeft size={16} aria-hidden="true" />
        Back
      </button>

      <h1
        style={{
          fontSize: 'var(--type-heading-size)',
          fontWeight: 'var(--type-heading-weight)',
          color: 'var(--text-primary)',
          marginTop: 'var(--space-3)',
        }}
      >
        Edit objective
      </h1>

      <Field label="Title">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => setTitleTouched(true)}
          placeholder="e.g. AWS certification"
          style={inputStyle}
        />
      </Field>
      {titleTouched && title.trim().length === 0 && (
        <p role="status" style={validationHelperStyle}>
          An objective needs a name.
        </p>
      )}

      <Field label="Why (optional)">
        <input
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          placeholder="e.g. career growth"
          style={inputStyle}
        />
      </Field>

      <Field label="Category">
        <CategoryPillSelect value={objective.category} onChange={handleCategoryChange} />
      </Field>

      <Field label="Level of effort">
        <EffortTileSelector value={objective.levelOfEffort} onChange={handleEffortChange} />
      </Field>

      <Field label="Target date" as="div">
        <DatePickerField
          value={targetDate}
          today={today}
          isDefaultValue={false}
          onChange={(date) => setTargetDate(date as IsoDate)}
        />
      </Field>

      <Field label="Cadence goal">
        <CadenceStepper value={objective.cadenceGoalPerWeek} onChange={handleCadenceChange} />
      </Field>

      <Field label="Priority">
        <PrioritySegmentedControl value={objective.priority ?? 'medium'} onChange={handlePriorityChange} />
      </Field>

      {objective.state !== 'archived' && (
        <Field label="Status">
          <StatusSegmentedControl
            value={objective.state === 'on_hold' ? 'on_hold' : 'active'}
            onChange={handleStatusChange}
          />
        </Field>
      )}

      <Field label="Reminder">
        <ReminderSchedulePicker value={objective.reminder} onChange={handleReminderChange} />
      </Field>

      <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-6)' }}>
        <PrimaryButton onClick={() => void saveHeader()} disabled={!canSaveHeader}>
          Save changes
        </PrimaryButton>
        <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
      </div>

      <div style={{ marginTop: 'var(--space-8)' }}>
        <h2
          style={{
            fontSize: 'var(--type-caption-size)',
            letterSpacing: 'var(--type-caption-tracking)',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            fontWeight: 600,
            margin: 0,
          }}
        >
          Actions
        </h2>

        {objective.actions.map((action) => (
          <EditableActionRow
            key={action.actionId}
            title={action.title}
            status={action.status}
            effortMinutes={action.effortMinutes}
            onSave={async (nextTitle) => {
              await api.editAction(objective.objectiveId, action.actionId, { title: nextTitle });
            }}
            onRemove={async () => {
              await api.removeAction(objective.objectiveId, action.actionId);
            }}
            onSetStatus={(status) => void handleSetActionStatus(action.actionId, status)}
            onSetEffort={(minutes) => void handleSetActionEffort(action.actionId, minutes)}
          />
        ))}

        <ActionRow
          label="Add an action"
          value={newActionTitle}
          onChange={setNewActionTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void addAction();
          }}
          canRemove={false}
          // Distinct from the existing-action rows' placeholder (both used
          // "e.g. Schedule the exam" originally) — harmless for sighted
          // users since only an empty field ever shows its placeholder, but
          // a genuine, if minor, ambiguity: any tooling or user searching
          // by that exact example text can no longer land on the wrong
          // field by accident.
          placeholder="e.g. Book the flight"
        />
        <div style={{ marginTop: 'var(--space-2)' }}>
          <button
            type="button"
            onClick={() => void addAction()}
            disabled={newActionTitle.trim().length === 0 || addingAction}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              color:
                newActionTitle.trim().length === 0 || addingAction
                  ? 'var(--text-disabled)'
                  : 'var(--hue-primary)',
              fontSize: 'var(--type-caption-size)',
              fontWeight: 600,
              minHeight: 'var(--touch-target-min)',
            }}
          >
            <Plus size={16} aria-hidden="true" />
            Add action
          </button>
        </div>
      </div>

      {
        // Phone's own LinkedHabitsSection (not desktop's copy — see this
        // screen's header comment) bakes in its own
        // `layout-page-margin-phone` horizontal margin, designed for
        // ObjectiveDetailScreen's edge-to-edge <main>. This screen's <main>
        // already carries that same margin as padding, so the wrapper below
        // cancels the section's own horizontal margin to keep it flush with
        // every other field on this page rather than doubly inset.
      }
      <div style={{ margin: '0 calc(-1 * var(--layout-page-margin-phone))' }}>
        <LinkedHabitsSection linkedHabits={objective.linkedHabits} habitsById={habitsById} />
      </div>

      <PinnedToggleSection pinned={objective.pinned} onChange={handlePinnedChange} />

      <VisibilityTogglesSection
        includeInWeeklyReview={objective.includeInWeeklyReview}
        surfaceOnToday={objective.surfaceOnToday}
        ambientRewardOnComplete={objective.ambientRewardOnComplete}
        onChange={handleVisibilityChange}
      />

      {
        // Stays mounted through the archived transition (not gated on
        // objective.state === 'active') so its own local "just archived"
        // receipt state survives the parent's re-fetch once the domain
        // layer's subscribe -> reload cycle picks up the new
        // objective.archived event — otherwise the footer would unmount the
        // instant objective.state flips, and the §7.3 receipt would never
        // be visible long enough to read.
      }
      <ObjectiveLifecycleFooter
        objectiveId={objective.objectiveId}
        title={objective.title}
        alreadyArchived={objective.state === 'archived'}
        onArchived={() => (onArchived ?? onDone)()}
      />
    </main>
  );
}

/**
 * One existing action's row in edit mode: editable title (saved on blur if
 * changed — same instant-persist feel as the rest of the app, no separate
 * per-row save button), a remove button (removeAction), plus — Phase 6
 * parity — the three-state status pill and a per-action effort control on a
 * second line beneath the title. Local `value` mirrors the incoming title
 * but is intentionally not clobbered by the subscribe -> reload cycle
 * mid-edit (only re-syncs when the underlying title itself changes, e.g.
 * after this row's own save completes or another device edits it).
 */
function EditableActionRow({
  title,
  status,
  effortMinutes,
  onSave,
  onRemove,
  onSetStatus,
  onSetEffort,
}: {
  readonly title: string;
  readonly status: ActionDisplayStatus;
  readonly effortMinutes: number | null;
  readonly onSave: (nextTitle: string) => Promise<void>;
  readonly onRemove: () => Promise<void>;
  readonly onSetStatus: (status: ActionDisplayStatus) => void;
  readonly onSetEffort: (effortMinutes: number | null) => void;
}) {
  const [value, setValue] = useState(title);

  useEffect(() => {
    setValue(title);
  }, [title]);

  return (
    <div style={{ marginTop: 'var(--space-4)' }}>
      <ActionRow
        label="Action"
        value={value}
        onChange={setValue}
        onBlur={() => {
          const trimmed = value.trim();
          if (trimmed.length > 0 && trimmed !== title) {
            void onSave(trimmed);
          } else if (trimmed.length === 0) {
            // Don't silently save an emptied title — restore the last known
            // good value instead (removeAction is the explicit way to
            // delete a row; blurring to blank is not treated as "delete").
            setValue(title);
          }
        }}
        canRemove
        onRemove={() => void onRemove()}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginTop: 'var(--space-2)' }}>
        <ActionStatusPill status={status} onChange={onSetStatus} />
        <ActionEffortControl effortMinutes={effortMinutes} onChange={onSetEffort} />
      </div>
    </div>
  );
}

/**
 * Small per-action effort control (minutes, with a bars glyph derived from
 * the same 1-3 bucket scale as `ActionsTable.tsx`'s `EffortPopover` on
 * desktop) — phone doesn't need that popover's full chrome, just a compact
 * inline reveal since desktop's own effort control isn't exported for
 * direct reuse (`src/ui/desktop/objective-form/` stays untouched).
 */
function ActionEffortControl({
  effortMinutes,
  onChange,
}: {
  readonly effortMinutes: number | null;
  readonly onChange: (effortMinutes: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [localMinutes, setLocalMinutes] = useState(effortMinutes ?? 0);

  useEffect(() => {
    setLocalMinutes(effortMinutes ?? 0);
  }, [effortMinutes]);

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-label={`Effort: ${effortMinutes ? `${effortMinutes} minutes` : 'unset'}`}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          minHeight: 'var(--touch-target-min)',
          padding: '0 var(--space-2)',
        }}
      >
        <EffortBars filled={effortMinutesToLevel(effortMinutes)} total={3} />
        <span className="num" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
          {effortMinutes ? `${effortMinutes}m` : '—'}
        </span>
      </button>
      {open && (
        <div style={{ marginTop: 'var(--space-2)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <input
            type="number"
            min={0}
            aria-label="Effort minutes"
            value={localMinutes}
            onChange={(e) => setLocalMinutes(Number(e.target.value))}
            style={{
              width: '72px',
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-2)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border-subtle)',
              background: 'var(--surface-sunken)',
              color: 'var(--text-primary)',
              fontSize: 'var(--type-body-size)',
            }}
          />
          <button
            type="button"
            onClick={() => {
              onChange(localMinutes > 0 ? localMinutes : null);
              setOpen(false);
            }}
            style={{
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--hue-primary-vivid)',
              color: 'var(--text-on-accent)',
              fontWeight: 600,
              fontSize: 'var(--type-caption-size)',
            }}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
}

/** Same 1-3 display-bucket scale as `ActionsTable.tsx`'s local (non-exported)
 *  `effortMinutesToLevel` on desktop — display-only grouping for the bars
 *  glyph, no domain math. */
function effortMinutesToLevel(minutes: number | null): number {
  if (minutes === null) return 0;
  if (minutes <= 20) return 1;
  if (minutes <= 60) return 2;
  return 3;
}
